// app_client/src/pages/admin/Notifications.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Check, Pin, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DataTable from '../../components/common/DataTable';
import useAccountStore from '../../store/useAccountStore';

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
};

const Notifications = () => {
  const {
    fetchNotifications,
    fetchMyNotifications,
    markNotificationAsRead,
    toggleNotificationPin
  } = useAccountStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    const result = await fetchNotifications();
    if (result.success) {
      setItems(result.data);
    } else {
      setError(result.message);
    }
    setLoading(false);
  }, [fetchNotifications]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadNotifications();
    }, 0);

    return () => clearTimeout(timer);
  }, [loadNotifications]);

  const handleReadToggle = async (notification) => {
    const result = await markNotificationAsRead(notification._id, !notification.isRead);
    if (!result.success) {
      toast.error(result.message || 'Bildirim güncellenemedi.');
      return;
    }

    await loadNotifications();
    await fetchMyNotifications();
  };

  const handlePinToggle = async (notification) => {
    const result = await toggleNotificationPin(notification._id, !notification.isPinned);
    if (!result.success) {
      toast.error(result.message || 'Bildirim pin durumu güncellenemedi.');
      return;
    }

    await loadNotifications();
    await fetchMyNotifications();
  };

  const columns = [
    {
      key: 'createdAt',
      header: 'Tarih',
      cellClassName: 'whitespace-nowrap text-xs font-bold text-slate-500',
      render: (row) => formatDate(row.createdAt)
    },
    {
      key: 'title',
      header: 'Bildirim',
      render: (row) => (
        <div className={row.isRead ? 'font-semibold text-slate-600' : 'font-black text-slate-900'}>
          <p>{row.title}</p>
          <p className={`mt-1 text-xs leading-5 ${row.isRead ? 'font-medium text-slate-500' : 'font-bold text-slate-700'}`}>
            {row.message}
          </p>
        </div>
      )
    },
    {
      key: 'account',
      header: 'Cari',
      cellClassName: 'text-sm font-bold text-slate-600',
      render: (row) => row.account?.name || '-'
    },
    {
      key: 'status',
      header: 'Durum',
      render: (row) => (
        <span className={`rounded-xl px-3 py-1 text-[10px] font-black uppercase tracking-widest ${row.isRead ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-700'}`}>
          {row.isRead ? 'Okundu' : 'Okunmadı'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Aksiyon',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => handleReadToggle(row)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-emerald-50 hover:text-emerald-600"
            title={row.isRead ? 'Okunmadı işaretle' : 'Okundu işaretle'}
          >
            <Check size={16} />
          </button>
          <button
            type="button"
            onClick={() => handlePinToggle(row)}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-xl transition ${row.isPinned ? 'bg-amber-50 text-amber-600' : 'text-slate-400 hover:bg-amber-50 hover:text-amber-600'}`}
            title={row.isPinned ? 'Pini kaldır' : 'Pinle'}
          >
            <Pin size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-col gap-4 rounded-[2rem] border border-slate-100 bg-slate-50/60 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <Bell size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Bildirimler</h1>
            <p className="text-sm font-medium text-slate-500">Takip ve sistem bildirimleri</p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadNotifications}
          className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600 shadow-sm transition hover:bg-slate-100"
        >
          <RefreshCw size={16} />
          Yenile
        </button>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          error={error}
          emptyText="Bildirim bulunamadı"
        />
      </div>
    </div>
  );
};

export default Notifications;
